import React, { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import * as Routes from '@/modules/routes';
import SPAPageHeader from '../shared/SPAPageHeader';
import ReturnToTeacher from './ReturnToTeacher';
import SPAPill from '../shared/SPAPill';
import AssignmentsTable from './AssignmentsTable';
import CourseMatchPanel from './CourseMatchPanel';
import useAssignmentRows from './useAssignmentRows';
import useCourseMatch from './useCourseMatch';
import useRepairs from './useRepairs';
import RepairHistory from './RepairHistory';
import { defaultFilter, matchesFilter } from './repairs';
import type { RepairFilter } from './repairs';
import type { AssignmentRow, AssignmentStatus } from './useAssignmentRows';
import { GradeSyncLink } from './gradeSyncPath';
import { InlineStat, MetricLabel } from './Metric';
import SPASectionHeading from '../shared/SPASectionHeading';

interface TeacherPayload {
  staffer?: {
    id: number;
    name: string;
    email: string | null;
    subscriber: string | null;
    subscriber_id: number | null;
    classrooms: number;
    students: number;
  };
  lms?: { key: boolean; platforms: string[]; providers: string[]; courses: number };
  assignments?: { total: number; linked: number; unlinked: number; with_sections: number };
  error?: string;
}

// AIDEV-NOTE: Assignment counts by delivery state. Deliberately not summed into a student total — a
// teacher's classes overlap and their assignments share students, so adding per-assignment figures
// double-counts people. The honest teacher-level number is how many assignments are in each state.
const BREAKDOWN: { key: AssignmentStatus; label: string; tone?: 'error' }[] = [
  { key: 'rejected', label: 'Rejected by the LMS', tone: 'error' },
  { key: 'not_delivering', label: 'Not delivering', tone: 'error' },
  { key: 'partial', label: 'Partly delivering' },
  { key: 'delivering', label: 'Delivering' },
  { key: 'covered_by_twin', label: 'Covered by a duplicate' },
  { key: 'nothing_to_sync', label: 'Nothing to sync' },
  { key: 'not_in_lms', label: 'Made in TCI, not from the LMS' },
];

const TeacherView = () => {
  const { id = '' } = useParams();

  const { data, isLoading } = useQuery({
    enabled: id.trim().length > 0,
    queryFn: async (): Promise<TeacherPayload> => {
      const { data: body } = await axios.get(Routes.plato_api_sysadmin_grade_sync_teacher_path(id));

      return body.data;
    },
    queryKey: ['gradeSyncTeacher', id],
  });

  const filters = useMemo(() => ({ staffer_id: id }), [id]);
  const assignments = useAssignmentRows(filters, id.trim().length > 0);
  // AIDEV-NOTE: Without an LTI key no roster or gradebook can be read, so neither is asked for; the
  // banner says why and the table falls back to each assignment's finding.
  const hasKey = data?.lms?.key === true;
  const match = useCourseMatch(id, hasKey);
  const [picked, setFilter] = useState<RepairFilter | null>(null);

  const rows: AssignmentRow[] = assignments.data?.rows ?? [];
  const { checking, repairs } = useRepairs(id, assignments.data?.rows, match.data, hasKey);
  const filter = picked ?? defaultFilter(repairs);
  const visible = repairs == null ? rows : rows.filter(row => matchesFilter(filter, repairs.get(row.assignment_id)));
  const counts = BREAKDOWN.map(entry => ({
    ...entry, value: rows.filter(row => row.status === entry.key).length,
  })).filter(entry => entry.value > 0);

  const staffer = data?.staffer;
  const lms = data?.lms;

  return (
    <div className="tw-min-h-screen tw-bg-spa-bg tw-text-spa-on-bg">
      <div className="tw-mx-auto tw-max-w-[1400px] tw-px-6 tw-py-4">
        <GradeSyncLink className="tw-mb-2 tw-inline-block tw-text-sm" to="">
          ← Grade sync
        </GradeSyncLink>
        <SPAPageHeader
          actions={staffer != null && <ReturnToTeacher name={staffer.name} stafferId={id} />}
          title={staffer?.name ?? 'Teacher'}
        />

        {data?.error != null && <p className="tw-m-0 tw-text-sm tw-text-spa-error">{data.error}</p>}

        {staffer != null && (
          <p className="tw-mb-5 tw-mt-0 tw-text-sm tw-text-spa-secondary">
            {staffer.subscriber ?? 'No district'}
            <span className="tw-text-spa-tertiary">{` · staffer ${staffer.id}`}</span>
            {staffer.email != null && <span className="tw-text-spa-tertiary">{` · ${staffer.email}`}</span>}
          </p>
        )}

        {/* AIDEV-NOTE: A subscriber with no LTI key makes every assignment on the account read as
            broken, and none of the per-assignment repairs apply. It belongs at the top, once. */}
        {lms != null && !lms.key && (
          <p className="tw-mb-4 tw-mt-0 tw-text-sm tw-text-spa-error">
            This teacher&apos;s district has no LTI key, so nothing here can sync until that is set up.
            Every finding below follows from that.
          </p>
        )}

        {!isLoading && staffer != null && (
          <div className="tw-mb-5 tw-flex tw-flex-wrap tw-gap-8">
            <InlineStat label="LMS assignments" value={data?.assignments?.total ?? 0} />
            <InlineStat
              hint={(data?.assignments?.unlinked ?? 0) > 0 ? `${data?.assignments?.unlinked} never linked` : undefined}
              label="Linked to a column"
              value={data?.assignments?.linked ?? 0}
            />
            <InlineStat label="LMS courses" value={lms?.courses ?? 0} />
            <InlineStat hint="on the account" label="Classes" value={staffer.classrooms} />
            <InlineStat hint="on the account" label="Students" value={staffer.students} />
            {lms != null && lms.providers.length > 0 && (
              <div>
                <div className="tw-flex tw-gap-1">
                  {lms.providers.map(kind => <SPAPill key={kind} tone="neutral">{kind}</SPAPill>)}
                </div>
                <MetricLabel>Platform</MetricLabel>
              </div>
            )}
          </div>
        )}

        {/* AIDEV-NOTE: Captioned because it counts a different population from the row above it. The
            header counts every LMS assignment on the account; these count the ones with graded work or
            posts the LMS is refusing now, which is what the table below lists. Unlabelled, "54 assignments"
            over a breakdown summing to 55 reads as an error rather than two honest numbers. */}
        {counts.length > 0 && (
          <>
            <SPASectionHeading title={`Of the ${rows.length} with graded work or rejected posts`} />
            <div className="tw-mb-5 tw-flex tw-flex-wrap tw-gap-8">
              {counts.map(entry => (
                <InlineStat key={entry.key} label={entry.label} tone={entry.tone} value={entry.value} />
              ))}
            </div>
          </>
        )}

        {hasKey && (
          <CourseMatchPanel
            assignments={assignments.data?.rows}
            checking={checking}
            filter={filter}
            match={match}
            repairs={repairs}
            stafferId={id}
            onFilter={setFilter}
          />
        )}

        <AssignmentsTable
          isLoading={assignments.isFetching}
          match={null}
          repairs={repairs}
          rows={visible}
          showTeacher={false}
          stafferId={id}
        />

        <RepairHistory stafferId={id} />

        <p className="tw-mt-4 tw-text-sm">
          <GradeSyncLink
            to={`/troubleshoot?staffer_id=${id}`}
          >
            Open in troubleshoot, to filter by student or class →
          </GradeSyncLink>
        </p>
      </div>
    </div>
  );
};

export default TeacherView;
